import {
  FilesetResolver,
  HandLandmarker,
  type HandLandmarkerResult,
} from '@mediapipe/tasks-vision';
import { HAND } from '@config/constants';
import { getPerfProfile, isIOS } from './device';

export interface DetectionFrame {
  result: HandLandmarkerResult;
  emittedAt: number;
}

/**
 * Thin wrapper around MediaPipe HandLandmarker.
 *
 * Surfaces init failures via `lastError` so the UI can show them —
 * otherwise iOS Safari MediaPipe errors are invisible from the host.
 */
export class HandDetector {
  private landmarker: HandLandmarker | null = null;
  private initializing: Promise<void> | null = null;
  private lastVideoTime = -1;

  /** Populated when initialize() fails — surfaced to the UI. */
  public lastError: string | null = null;

  async initialize(): Promise<void> {
    if (this.landmarker) return;
    if (this.initializing) return this.initializing;

    this.initializing = (async () => {
      const profile = getPerfProfile();
      try {
        const fileset = await FilesetResolver.forVisionTasks(HAND.WASM_CDN);
        this.landmarker = await HandLandmarker.createFromOptions(fileset, {
          baseOptions: {
            modelAssetPath: HAND.MODEL_URL,
            delegate: profile.delegate,
          },
          runningMode: 'VIDEO',
          numHands: profile.numHands,
          minHandDetectionConfidence: HAND.MIN_HAND_DETECTION_CONFIDENCE,
          minHandPresenceConfidence: HAND.MIN_HAND_PRESENCE_CONFIDENCE,
          minTrackingConfidence: HAND.MIN_TRACKING_CONFIDENCE,
        });
        this.lastError = null;
      } catch (err) {
        this.lastError = err instanceof Error ? err.message : String(err);
        // Retry once on iOS with CPU delegate if GPU fails
        if (profile.delegate === 'GPU' && isIOS()) {
          try {
            const fileset = await FilesetResolver.forVisionTasks(HAND.WASM_CDN);
            this.landmarker = await HandLandmarker.createFromOptions(fileset, {
              baseOptions: { modelAssetPath: HAND.MODEL_URL, delegate: 'CPU' },
              runningMode: 'VIDEO',
              numHands: profile.numHands,
              minHandDetectionConfidence: HAND.MIN_HAND_DETECTION_CONFIDENCE,
              minHandPresenceConfidence: HAND.MIN_HAND_PRESENCE_CONFIDENCE,
              minTrackingConfidence: HAND.MIN_TRACKING_CONFIDENCE,
            });
            this.lastError = null;
          } catch (err2) {
            this.lastError = err2 instanceof Error ? err2.message : String(err2);
          }
        }
      }
    })();

    try {
      await this.initializing;
    } finally {
      this.initializing = null;
    }
  }

  isReady(): boolean {
    return this.landmarker !== null;
  }

  detect(video: HTMLVideoElement, timestampMs: number): DetectionFrame | null {
    if (!this.landmarker) return null;
    if (video.readyState < 2) return null;
    if (!video.videoWidth || !video.videoHeight) return null;
    if (timestampMs === this.lastVideoTime) return null;
    this.lastVideoTime = timestampMs;
    try {
      const result = this.landmarker.detectForVideo(video, timestampMs);
      return { result, emittedAt: performance.now() };
    } catch (err) {
      this.lastError = err instanceof Error ? err.message : String(err);
      return null;
    }
  }

  dispose(): void {
    this.landmarker?.close();
    this.landmarker = null;
  }
}
